import React from "react";
import { getBeatPattern, beatAccentLevel, DEFAULT_BEAT } from "../lib/beatSignature";

// Web Audio metronome with time-signature awareness.
// Each beat click is scheduled ahead-of-time so the timing is sample-accurate.
// Accent rules come from `beat` (2/4, 3/4, 4/4, 6/8, 12/8).
export const useMetronome = () => {
  const [on, setOn] = React.useState(false);
  const [bpm, setBpm] = React.useState(100);
  const [beat, setBeat] = React.useState(DEFAULT_BEAT);
  const ctxRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const nextTickRef = React.useRef(0);
  const beatRef = React.useRef(0);
  // Keep a live ref to the current beat sig so the scheduler always reads the
  // latest pattern even when the user changes it while the metronome is running.
  const beatSigRef = React.useRef(DEFAULT_BEAT);
  React.useEffect(() => { beatSigRef.current = beat; }, [beat]);

  const schedule = React.useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const lookahead = 0.1;
    while (nextTickRef.current < ctx.currentTime + lookahead) {
      const time = nextTickRef.current;
      const pattern = getBeatPattern(beatSigRef.current);
      const idxInMeasure = beatRef.current % pattern.beats;
      const level = beatAccentLevel(beatSigRef.current, idxInMeasure);
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      // 0 = primary (loud), 1 = mid, 2 = quiet
      const freq = level === 0 ? 1500 : level === 1 ? 1200 : 1000;
      const peakGain = level === 0 ? 0.55 : level === 1 ? 0.4 : 0.28;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, time);
      gain.gain.exponentialRampToValueAtTime(peakGain, time + 0.001);
      gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.05);
      osc.connect(gain).connect(ctx.destination);
      osc.start(time);
      osc.stop(time + 0.06);
      beatRef.current += 1;
      nextTickRef.current += 60 / bpm;
    }
  }, [bpm]);

  const start = React.useCallback(() => {
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") ctx.resume();
    beatRef.current = 0;
    nextTickRef.current = ctx.currentTime + 0.05;
    timerRef.current = setInterval(schedule, 25);
    setOn(true);
  }, [schedule]);

  const stop = React.useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setOn(false);
  }, []);

  const toggle = React.useCallback(() => {
    if (on) stop();
    else start();
  }, [on, start, stop]);

  // Restart timer when bpm changes while running. Beat-signature changes are
  // handled live via beatSigRef so we never need to restart for them.
  React.useEffect(() => {
    if (on) {
      if (timerRef.current) clearInterval(timerRef.current);
      const ctx = ctxRef.current;
      if (ctx) {
        beatRef.current = 0;
        nextTickRef.current = ctx.currentTime + 0.05;
      }
      timerRef.current = setInterval(schedule, 25);
    }
    return () => {};
  }, [bpm, on, schedule]);

  React.useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (ctxRef.current) ctxRef.current.close();
  }, []);

  return { on, bpm, setBpm, beat, setBeat, toggle, start, stop };
};
